const FILTERED = "[Filtered]";
const PATH_SEGMENT = "[^/?#\\s\"'\\\\&<>]+";

// Routes whose path segment grants access without a login.
const TOKEN_PATHS = [
  [new RegExp(`/lieferdatum/${PATH_SEGMENT}`, "g"), "/lieferdatum/:token"],
  [new RegExp(`/display/${PATH_SEGMENT}`, "g"), "/display/:token"],
  [
    new RegExp(`/public/syringes/delivery-date/${PATH_SEGMENT}`, "g"),
    "/public/syringes/delivery-date/:token",
  ],
];

// Query values hold customer search terms, so all of them are masked.
const QUERY_VALUE = /([?&][^=&\s"'\\#]+=)[^&\s"'\\#]*/g;

// These fields hold a bare query string that QUERY_VALUE cannot recognize.
const QUERY_FIELDS = new Set(["query_string", "url.query", "http.query"]);

// A serialized AxiosError carries the request body as config.data.
const SERIALIZED_BODY = /\\?"data\\?":\s*\\?"(?:[^"\\]|\\.)*"/g;

function scrubString(value) {
  let scrubbed = value;
  for (const [pattern, replacement] of TOKEN_PATHS) {
    scrubbed = scrubbed.replace(pattern, replacement);
  }
  return scrubbed
    .replace(QUERY_VALUE, `$1${FILTERED}`)
    .replace(SERIALIZED_BODY, `"data":"${FILTERED}"`);
}

function isPlainObject(value) {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isRequestConfig(value) {
  return "url" in value && "method" in value;
}

/**
 * Returns a copy with tokens, query values and request bodies masked in every
 * string. Inputs stay untouched because breadcrumbs reference live app objects.
 */
export function scrubValue(value, seen = new WeakMap()) {
  if (typeof value === "string") return scrubString(value);
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return seen.get(value);

  if (Array.isArray(value)) {
    const copy = [];
    seen.set(value, copy);
    value.forEach((item) => copy.push(scrubValue(item, seen)));
    return copy;
  }
  if (!isPlainObject(value)) return value;

  const copy = {};
  seen.set(value, copy);
  for (const [key, item] of Object.entries(value)) {
    const isSensitive =
      QUERY_FIELDS.has(key) || (key === "data" && isRequestConfig(value));
    copy[key] = isSensitive ? FILTERED : scrubValue(item, seen);
  }
  return copy;
}

export function scrubEvent(event) {
  const scrubbed = scrubValue(event);
  if (scrubbed.user) delete scrubbed.user.ip_address;
  return scrubbed;
}

export function scrubBreadcrumb(breadcrumb) {
  const scrubbed = scrubValue(breadcrumb);
  // Raw console arguments can hold an AxiosError including its request body.
  // The joined message stays available for debugging.
  if (scrubbed.category === "console" && scrubbed.data) {
    delete scrubbed.data.arguments;
  }
  return scrubbed;
}

/**
 * Intentional aborts and 401s after a failed refresh are normal flow. A 401
 * while the session is alive (e.g. rejected right after a refresh) or on the
 * login itself points to a real problem and stays visible.
 */
export function isExpectedError(error, { sessionDead }) {
  if (!error) return false;
  if (
    error.code === "ERR_CANCELED" ||
    error.name === "CanceledError" ||
    error.name === "AbortError"
  ) {
    return true;
  }
  return (
    error.isAxiosError === true &&
    error.response?.status === 401 &&
    sessionDead &&
    !/\/auth\/login$/.test(error.config?.url || "")
  );
}
